import {useCallback, useEffect, useMemo, useState} from "react"
import axios from "axios"
import {Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table} from "react-bootstrap"
import {useTranslation} from "react-i18next"

import {useAuth} from "../../../context/AuthProvider"
import BuildingOverviewComponent from "../../BuildingView/BuildingComponent"

type Building = {
    _id: string
    identifier: string
    name: string
    campus: string
    campusMapUrl: string
    latitude: string
    longitude: string
    street: string
    postcode: string
    stopsNearby: string[]
    isDummy: boolean
    imageAttribution?: ImageAttribution
}

type ImageAttribution = {
    sourceUrl: string
    author: string
    license: string
    licenseUrl: string
    note: string
}

type BuildingForm = Omit<Building, "_id" | "stopsNearby" | "imageAttribution"> & {
    stopsNearby: string
    imageAttribution: ImageAttribution
}

const emptyImageAttribution: ImageAttribution = {
    sourceUrl: "",
    author: "",
    license: "",
    licenseUrl: "",
    note: "",
}

const emptyForm: BuildingForm = {
    identifier: "",
    name: "",
    campus: "",
    campusMapUrl: "",
    latitude: "",
    longitude: "",
    street: "",
    postcode: "",
    stopsNearby: "",
    isDummy: false,
    imageAttribution: emptyImageAttribution,
}

const campusByPrefix: Record<string, {campus: string; campusMapUrl: string}> = {
    B: {
        campus: "Botanischer Garten",
        campusMapUrl: "https://www.tu-darmstadt.de/universitaet/campus/botanischer_garten_1/index.de.jsp",
    },
    L: {
        campus: "Lichtwiese",
        campusMapUrl: "https://www.tu-darmstadt.de/universitaet/campus/lichtwiese_2/index.de.jsp",
    },
    S: {
        campus: "Stadtmitte",
        campusMapUrl: "https://www.tu-darmstadt.de/universitaet/campus/stadtmitte_3/index.de.jsp",
    },
    W: {
        campus: "Windkanal",
        campusMapUrl: "https://www.tu-darmstadt.de/universitaet/campus/windkanal_flugplatz/index.de.jsp",
    },
}

export default function BuildingsDebugViewComponent() {
    const {t} = useTranslation("admin")
    const auth = useAuth()
    const canManage = auth.hasAnyPermission(["examOfficeEmployee", "examOfficeAssistant"])
    const [buildings, setBuildings] = useState<Building[]>([])
    const [form, setForm] = useState<BuildingForm>(emptyForm)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [query, setQuery] = useState("")
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<{type: "danger" | "success"; text: string} | null>(null)

    const load = useCallback(async () => {
        if (!canManage) {
            setLoading(false)
            return
        }
        setLoading(true)
        try {
            const response = await axios.get<Building[]>("/api/rooms/buildings", auth.axiosAuthHeader())
            setBuildings(response.data)
            setMessage(null)
        } catch {
            setMessage({type: "danger", text: t("buildings.messages.loadError")})
        } finally {
            setLoading(false)
        }
    }, [auth, canManage, t])

    useEffect(() => {
        void load()
    }, [load])

    const filtered = useMemo(() => {
        const term = query.trim().toLocaleLowerCase()
        if (!term) return buildings
        return buildings.filter(building => [
            building.identifier, building.name, building.campus,
            building.street, building.postcode,
        ].some(value => value?.toLocaleLowerCase().includes(term)))
    }, [buildings, query])
    const reset = () => {
        setEditingId(null)
        setForm(emptyForm)
    }

    const edit = (building: Building) => {
        const campusData = campusByPrefix[building.identifier[0]]
        setEditingId(building._id)
        setForm({
            ...building,
            campus: campusData?.campus ?? building.campus,
            campusMapUrl: campusData?.campusMapUrl ?? building.campusMapUrl,
            stopsNearby: building.stopsNearby.join(", "),
            imageAttribution: {
                ...emptyImageAttribution,
                ...building.imageAttribution,
            },
        })
        window.scrollTo({top: 0, behavior: "smooth"})
    }

    const save = async () => {
        setSaving(true)
        setMessage(null)
        try {
            const payload = {
                ...form,
                stopsNearby: form.stopsNearby.split(",").map(value => value.trim()).filter(Boolean),
            }
            if (editingId) {
                await axios.put(`/api/rooms/buildings/${editingId}`, payload, auth.axiosAuthHeader())
            } else {
                await axios.post("/api/rooms/buildings", payload, auth.axiosAuthHeader())
            }
            setMessage({type: "success", text: t(editingId
                ? "buildings.messages.updated"
                : "buildings.messages.created")})
            reset()
            await load()
        } catch (error: unknown) {
            setMessage({
                type: "danger",
                text: axios.isAxiosError(error)
                    ? error.response?.data?.msg ?? t("buildings.messages.saveError")
                    : t("buildings.messages.saveError"),
            })
        } finally {
            setSaving(false)
        }
    }

    if (!canManage) return <div className="component"><Alert variant="danger">
        {t("buildings.messages.forbidden")}
    </Alert></div>

    const requiredComplete = [
        form.identifier, form.name, form.campus, form.campusMapUrl,
        form.latitude, form.longitude, form.street, form.postcode,
    ].every(value => value.trim())

    const updateIdentifier = (identifier: string) => {
        const normalized = identifier.toUpperCase()
        const campusData = campusByPrefix[normalized[0]]
        setForm({
            ...form,
            identifier: normalized,
            campus: campusData?.campus ?? "",
            campusMapUrl: campusData?.campusMapUrl ?? "",
        })
    }

    return <div className="component">
        <Row className="g-3">
            <Col xs={12} xl={5}>
                <Card>
                    <Card.Header><h3 className="mb-0">
                        {t(editingId ? "buildings.edit" : "buildings.create")}
                    </h3></Card.Header>
                    <Card.Body>
                        {message && <Alert variant={message.type}>{message.text}</Alert>}
                        <Row className="g-3">
                            <Col xs={12} md={6}>
                                <Form.Label>{t("buildings.fields.identifier")}</Form.Label>
                                <Form.Control value={form.identifier}
                                    onChange={event => updateIdentifier(event.target.value)}/>
                            </Col>
                            {([
                                ["name", t("buildings.fields.name")],
                                ["latitude", t("buildings.fields.latitude")],
                                ["longitude", t("buildings.fields.longitude")],
                                ["street", t("buildings.fields.street")],
                                ["postcode", t("buildings.fields.postcode")],
                                ["stopsNearby", t("buildings.fields.stopsNearby")],
                            ] as [keyof BuildingForm, string][]).map(([field, label]) =>
                                <Col xs={12} md={field === "name" || field === "stopsNearby" ? 12 : 6} key={field}>
                                    <Form.Label>{label}</Form.Label>
                                    <Form.Control value={String(form[field])}
                                        onChange={event => setForm({...form, [field]: event.target.value})}/>
                                </Col>
                            )}
                            <Col xs={12}>
                                <Form.Check label={t("buildings.fields.dummy")}
                                    checked={form.isDummy}
                                    onChange={event => setForm({...form, isDummy: event.target.checked})}/>
                            </Col>
                            <Col xs={12}>
                                <hr/>
                                <h5>{t("buildings.imageAttribution.title")}</h5>
                                <Form.Text>{t("buildings.imageAttribution.hint")}</Form.Text>
                            </Col>
                            {([
                                ["sourceUrl", t("buildings.imageAttribution.sourceUrl")],
                                ["author", t("buildings.imageAttribution.author")],
                                ["license", t("buildings.imageAttribution.license")],
                                ["licenseUrl", t("buildings.imageAttribution.licenseUrl")],
                                ["note", t("buildings.imageAttribution.note")],
                            ] as [keyof ImageAttribution, string][]).map(([field, label]) =>
                                <Col xs={12}
                                    md={field === "note" || field === "sourceUrl" ? 12 : 6}
                                    key={field}>
                                    <Form.Label>{label}</Form.Label>
                                    <Form.Control
                                        type={field.endsWith("Url") ? "url" : "text"}
                                        value={form.imageAttribution[field]}
                                        onChange={event => setForm({
                                            ...form,
                                            imageAttribution: {
                                                ...form.imageAttribution,
                                                [field]: event.target.value,
                                            },
                                        })}/>
                                </Col>
                            )}
                        </Row>
                        <div className="d-flex gap-2 mt-3">
                            <Button onClick={save} disabled={saving || !requiredComplete}>
                                {saving && <Spinner size="sm" className="me-2"/>}
                                {t(editingId ? "buildings.saveChanges" : "buildings.submit")}
                            </Button>
                            {editingId && <Button variant="secondary" onClick={reset}>
                                {t("buildings.cancel")}
                            </Button>}
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col xs={12} xl={7}>
                <Card>
                    <Card.Header><h3 className="mb-0">{t("buildings.overview")}</h3></Card.Header>
                    <Card.Body>
                        <Form.Control type="search" className="mb-3"
                            placeholder={t("buildings.search")}
                            value={query} onChange={event => setQuery(event.target.value)}/>
                        {loading ? <Spinner size="sm"/> : <Table responsive hover>
                            <thead><tr><th>{t("buildings.columns.identifier")}</th>
                                <th>{t("buildings.columns.name")}</th>
                                <th>{t("buildings.columns.location")}</th><th/></tr></thead>
                            <tbody>{filtered.map(building => <tr key={building._id}>
                                <td><Badge bg={building.isDummy ? "warning" : "secondary"}>
                                    {building.identifier}
                                </Badge></td>
                                <td>{building.name}</td>
                                <td>{building.campus}<br/><small>{building.street}, {building.postcode}</small></td>
                                <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                        <Button size="sm" variant="outline-secondary"
                                            onClick={() => {
                                                document.getElementById(`building-preview-${building._id}`)
                                                    ?.scrollIntoView({behavior: "smooth", block: "start"})
                                            }}>
                                            <i className="bi bi-eye me-1"/>{t("buildings.preview")}
                                        </Button>
                                        <Button size="sm" variant="outline-primary"
                                            onClick={() => edit(building)}>{t("buildings.editAction")}</Button>
                                    </div>
                                </td>
                            </tr>)}</tbody>
                        </Table>}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
        {!loading && buildings.map(building =>
            <div
                id={`building-preview-${building._id}`}
                className="mt-3"
                key={building._id}
                style={{scrollMarginTop: "1rem"}}
            >
                <BuildingOverviewComponent building={building}/>
            </div>
        )}
    </div>
}
